import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  PAGE_ROUTE_SOPS,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { MANAGE_PROFILE_SOP_SECTIONS } from "./constants/manageProfileSopContent";
import { MANAGE_PROFILE_SOP_IMAGES } from "./constants/manageProfileSopImages";
import type { ManageProfileSopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const ManageProfileSopImage: React.FC<{
  image: ManageProfileSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const ManageProfileSopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const runBooksPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="manage-profile-sop-page"
        eyebrow={t("manage-profile-sop-page-eyebrow", "Run books")}
        title={t(
          "manage-profile-sop-page-title",
          "How to manage your personal profile"
        )}
        intro={t(
          "manage-profile-sop-page-intro",
          "Update your name, phone, photo, and password. This guide is for your own account, not company or store settings."
        )}
      />

      <PublicInfoCallout testId="manage-profile-sop-page-note">
        {t(
          "manage-profile-sop-page-note",
          "Screenshots hide personal details for privacy. Your own profile page will show your real name, email, and phone."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {MANAGE_PROFILE_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`manage-profile-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            {section.introKey != null ? (
              <Typography
                variant="body1"
                sx={{ ...publicInfoBodySx, mb: 2 }}
                data-testid={`manage-profile-sop-intro-${section.id}`}
              >
                {t(section.introKey, section.introFallback ?? "")}
              </Typography>
            ) : null}
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? MANAGE_PROFILE_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <ManageProfileSopImage
                        image={image}
                        testId={`manage-profile-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t("manage-profile-sop-page-cta-title", "New to Kivunova FMS?")}
        testId="manage-profile-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "manage-profile-sop-page-cta-body",
            "Read the full setup guide for registration, stores, and everyday tasks."
          )}{" "}
          <MuiLink
            component={Link}
            to={runBooksPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-profile-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("manage-profile-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-profile-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("manage-profile-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-profile-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default ManageProfileSopPage;
